import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, Subject, takeUntil } from 'rxjs';
import {
  AppNotification,
  NotificationSocketEnvelope,
} from '../models/notification-api.models';
import { mapNotificationFromApi } from '../utils/notification.utils';
import { ApiConfigService } from './api-config.service';
import { AuthService } from './auth.service';
import { NotificationCounterService } from './notification-counter.service';

@Injectable({ providedIn: 'root' })
export class NotificationWebSocketService implements OnDestroy {
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly liveMessagesSubject = new BehaviorSubject<AppNotification[]>([]);
  private readonly destroy$ = new Subject<void>();
  private currentUserId: string | null = null;

  readonly liveMessages$ = this.liveMessagesSubject.asObservable();

  constructor(
    private readonly authService: AuthService,
    private readonly apiConfig: ApiConfigService,
    private readonly counter: NotificationCounterService,
  ) {
    combineLatest([this.authService.isLoggedIn$, this.authService.userProfile$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([loggedIn, profile]) => {
        const userId = profile?.id?.trim();
        if (!loggedIn || !userId) {
          this.disconnect();
          return;
        }

        if (userId !== this.currentUserId) {
          this.connect(userId);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }

  removeLiveMessage(id: string): void {
    this.liveMessagesSubject.next(
      this.liveMessagesSubject.value.filter((message) => message.id !== id),
    );
  }

  private connect(userId: string): void {
    this.disconnect(false);
    this.currentUserId = userId;

    try {
      const url = this.apiConfig.buildNotificationWebSocketUrl(userId);
      this.socket = new WebSocket(url);

      this.socket.onmessage = (event) => {
        this.handleMessage(event.data as string);
      };

      this.socket.onerror = () => {
        this.scheduleReconnect();
      };

      this.socket.onclose = () => {
        this.scheduleReconnect();
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  private handleMessage(raw: string): void {
    try {
      const payload = JSON.parse(raw) as NotificationSocketEnvelope;

      if (payload.isInitial) {
        this.counter.setValue(payload.unRead ?? 0);
        return;
      }

      if (payload.content && typeof payload.content === 'object') {
        const notification = mapNotificationFromApi(payload.content as Record<string, unknown>);
        if (!notification.id) {
          return;
        }

        const existing = this.liveMessagesSubject.value;
        if (!existing.some((item) => item.id === notification.id)) {
          this.liveMessagesSubject.next([notification, ...existing]);
        }
        this.counter.increment();
      }
    } catch {
      // ignore malformed socket payloads
    }
  }

  private scheduleReconnect(): void {
    if (!this.currentUserId || !this.authService.isAuthenticated()) {
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(() => {
      if (this.currentUserId && this.authService.isAuthenticated()) {
        this.connect(this.currentUserId);
      }
    }, 5000);
  }

  private disconnect(clearUser = true): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (clearUser) {
      this.currentUserId = null;
    }

    if (this.socket) {
      const socket = this.socket;
      socket.onclose = null;
      socket.onerror = null;

      try {
        if (!clearUser && this.currentUserId && socket.readyState === WebSocket.OPEN) {
          socket.send(
            JSON.stringify({
              type: 'disconnect',
              userId: this.currentUserId,
              timestamp: new Date().toISOString(),
            }),
          );
        }
      } catch {
        // ignore
      }

      socket.close();
      this.socket = null;
    }

    if (clearUser) {
      this.liveMessagesSubject.next([]);
      this.counter.reset();
    }
  }
}
